
import { defineComponent, ref, computed, nextTick, onMounted } from "vue";

import { store } from "@/store";

import { updateImage, scaledDPI } from "@/canvas-api/UserObjects/Image";

import usePickr from "@/composables/usePickr";

import { ColorPickrState } from "@/types";

export default defineComponent({
  name: "EditImage",
  emits: ["reCropImage"],
  setup() {
    const dpi = computed(() => store.getters.dpi);
    const currentActiveObject = computed(() => store.getters.currentActiveObject);
    const colorPickrState = computed<ColorPickrState>(() => store.getters.colorPickrState);

    const imageWidth = ref(0);
    const imageHeight = ref(0);
    const resolution = ref(0);
    const resolutionWidth = ref(0);
    const resolutionHeight = ref(0);

    const borderSlider = ref(0);
    const opacitySlider = ref(0);
    const borderWidthRange = ref<HTMLInputElement>();
    const opacityRange = ref<HTMLInputElement>();
    const borderElement = ref<HTMLDivElement>();
    onMounted(() => {
      calculateDimsAndRes();

      if (borderWidthRange.value) {
        borderWidthRange.value.value = (currentActiveObject.value.strokeWidth! * 10).toString();
      }

      if (opacityRange.value) {
        opacityRange.value.value = (100 - currentActiveObject.value.opacity! * 100).toString();
        opacitySlider.value = 100 - currentActiveObject.value.opacity! * 100;
      }

      if (borderElement.value) {
        borderElement.value.style.background = currentActiveObject.value.stroke!;
      }
    });

    const pxToInches = (value: number) => {
      return value / dpi.value;
    };

    const calculateDimsAndRes = () => {
      const width = currentActiveObject.value.width * currentActiveObject.value.scaleX!;
      const height = currentActiveObject.value.height * currentActiveObject.value.scaleY!;

      imageWidth.value = Number(pxToInches(width).toFixed(2));
      imageHeight.value = Number(pxToInches(height).toFixed(2));

      resolution.value = Math.round(currentActiveObject.value.width / imageWidth.value);
      resolutionWidth.value = Math.round(currentActiveObject.value.width / imageWidth.value);
      resolutionHeight.value = Math.round(currentActiveObject.value.height / imageHeight.value);
    };

    const handleWidth = (e: { target: HTMLInputElement }) => {
      const value = Number(e.target.value);

      if (!Number.isNaN(value) && value > 0) {
        const widthInPx = value * dpi.value;
        const currentWidth = currentActiveObject.value.width;
        const scaleX = widthInPx / currentWidth;
        updateImage("scaleX", scaleX);

        resolution.value = Math.round(currentActiveObject.value.width / imageWidth.value);
        resolutionWidth.value = Math.round(currentActiveObject.value.width / imageWidth.value);
      }
    };

    const handleHeight = (e: { target: HTMLInputElement }) => {
      const value = Number(e.target.value);

      if (!Number.isNaN(value) && value > 0) {
        const heightInPx = value * dpi.value;
        const currentHeight = currentActiveObject.value.height;
        const scaleY = heightInPx / currentHeight;
        updateImage("scaleY", scaleY);

        resolutionHeight.value = Math.round(currentActiveObject.value.height / imageHeight.value);
      }
    };

    const resetDPI = async () => {
      await updateImage("scaleX", scaledDPI);
      await updateImage("scaleY", scaledDPI);
      await nextTick();

      calculateDimsAndRes();
    };

    const handleBorderWidth = (e: { target: HTMLInputElement }): void => {
      borderSlider.value = Number(e.target.value) / 10;

      if (currentActiveObject.value) {
        updateImage("strokeWidth", borderSlider.value);
      }
    };

    const handleOpacity = (e: { target: HTMLInputElement }): void => {
      opacitySlider.value = Number(e.target.value);

      if (currentActiveObject.value) {
        updateImage("opacity", 1 - opacitySlider.value / 100);
      }
    };

    const handleToggleColorPickr = (e: { target: HTMLDivElement }): void => {
      const TARGET = e.target;

      switch (TARGET.getAttribute("id")) {
        case "border-circle": {
          let payload = {
            ...colorPickrState.value,
            isOpen: false,
            id: TARGET.getAttribute("id")!,
            property: {
              name: "stroke",
              value: TARGET.style.backgroundColor,
            },
          };

          usePickr(payload);
          break;
        }
        default: {
          throw new Error("Missing target id!, Customize.vue");
        }
      }
    };

    const prevLabel = ref<HTMLLabelElement>();
    onMounted(() => {
      const strokeDashArray = currentActiveObject.value.strokeDashArray
        ? currentActiveObject.value.strokeDashArray[0]
        : null;

      switch (strokeDashArray) {
        case 2: {
          const borderStyleInput = document.getElementById("dotted");

          if (borderStyleInput) {
            const label = borderStyleInput.parentNode as HTMLLabelElement;
            label.className = "option-selected";

            prevLabel.value = label;
          }
          break;
        }
        case 6: {
          const borderStyleInput = document.getElementById("dashed");

          if (borderStyleInput) {
            const label = borderStyleInput.parentNode as HTMLLabelElement;
            label.className = "option-selected";

            prevLabel.value = label;
          }
          break;
        }
        default: {
          const borderStyleInput = document.getElementById("solid");

          if (borderStyleInput) {
            const label = borderStyleInput.parentNode as HTMLLabelElement;
            label.className = "option-selected";

            prevLabel.value = label;
          }

          break;
        }
      }
    });

    const setBorderStyle = (e: { target: HTMLInputElement }) => {
      const value = e.target.value;

      switch (value) {
        case "solid": {
          updateImage("strokeDashArray", undefined);
          break;
        }
        case "dotted": {
          updateImage("strokeDashArray", [2, 5]);
          break;
        }
        case "dashed": {
          updateImage("strokeDashArray", [6, 5]);
          break;
        }
      }

      const label = e.target.parentNode as HTMLLabelElement;

      if (prevLabel.value) {
        prevLabel.value.className = "";
      }

      if (label) {
        label.className = "option-selected";
        prevLabel.value = label;
      }
    };

    return {
      currentActiveObject,
      imageWidth,
      imageHeight,
      resolution,
      resolutionWidth,
      resolutionHeight,
      borderSlider,
      opacitySlider,
      borderWidthRange,
      opacityRange,
      borderElement,
      handleWidth,
      handleHeight,
      resetDPI,
      handleBorderWidth,
      handleOpacity,
      handleToggleColorPickr,
      setBorderStyle,
    };
  },
});
